/**
 * מנוי ומכסות — what a customer bought, and the four things an operator may change about it.
 *
 * The whole reading half of the screen is four numbers said plainly: companies purchased, companies
 * used, seats purchased, seats used. 09 §5.1's rule about figures — *"313 is not 313"* — is why every
 * line carries all three of purchased, used and remaining rather than a single usage count that
 * nobody can reconcile against an invoice.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * WHAT MOVED ONTO THIS SCREEN, AND WHAT DELIBERATELY DID NOT
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * ON: the plan, the SKUs, the trial and the two limits. All four are ENTITLEMENT — what the customer
 * may do and how much of it — which is exactly what this screen already displayed and could not
 * change. A capacity screen an operator cannot act on sends them to a colleague with a database.
 *
 * NOT ON: discounts. 08 §8.4 / CF-66 keep money out of entitlement entirely — *"two tenants identical
 * but for `discount_pct` must produce byte-identical decisions"* — and this screen's original header
 * named a capacity view showing a price as the first place the two get welded together again. So the
 * discount routes live on their own address, `PlatformCommercialScreen`, and the only thing that
 * crosses is a link.
 *
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 * A LIMIT MAY BE SET BELOW CURRENT USAGE, AND THE SCREEN SAYS SO BEFORE THE CLICK
 * ══════════════════════════════════════════════════════════════════════════════════════════════
 *
 * `capacity.ts` rule 1: capacity refuses CREATION and never degrades what exists. Rule 2:
 * over-capacity is a real state. So lowering a limit under current usage SUCCEEDS, reports how far
 * over the customer now is, and deletes nothing — and an operator who was not told that in advance
 * would reasonably believe they had just destroyed eighteen companies.
 *
 * `null` is a meaningful value on both limit forms: it means UNMETERED, and it is a different thing
 * from an empty field. The control says which, because `row?.limit ?? null` turning a failed read
 * into "unlimited" is precisely the one-character defect the server-side union exists against.
 *
 * ── AND NOTHING HERE COMPUTES A VERDICT ────────────────────────────────────────────────────────
 *
 * "May another company be created" is `checkCapacity` on the server. This renders counts, sends
 * changes, and prints whatever answer comes back.
 */

import { useState } from 'react';
import type { FormEvent } from 'react';
import { AppShell } from '../shell/AppShell';
import { useWorkContext } from '../shell/ContextProvider';
import { ActionFailureNotice } from '../components/ActionFailureNotice';
import { CapacityPanel } from '../components/CapacityPanel';
import { FactList } from '../components/FactList';
import { StateBlock } from '../components/StateBlock';
import { StatusChip } from '../components/StatusChip';
import { SurfaceNotice } from '../components/SurfaceNotice';
import type { SubscriptionState, TenantLifecycleRefusal, TenantStatus } from '../api/contract';
import { SUBSCRIPTION_STATES, TENANT_STATUSES } from '../api/contract';
import { formatInstantHe, subscriptionStateHe } from '../api/adminVocabulary';
import { subscriptionStatesHe, tenantLifecycleRefusalText } from '../api/lifecycleText';
import type { ActionFailure } from '../api/actionOutcome';
import { classifyActionError } from '../api/actionOutcome';
import { callOperation } from '../api/operations';
import { fixtureSubscription, fixturesEnabled, fixtureTenants } from '../api/fixtures';
import { routes } from '../routes';

export interface PlatformSubscriptionScreenProps {
  readonly tenantUid: string;
}

const TITLE_HE = 'מנוי ומכסות';

/** What was just changed, named in the words an operator used to ask for it. */
type Changed = 'plan' | 'sku' | 'trial' | 'trialExtension' | 'companyLimit' | 'seatLimit' | 'state';

const CHANGED_HE: Readonly<Record<Changed, string>> = {
  plan: 'התכנית שויכה למנוי',
  sku: 'הפריט נוסף למנוי',
  trial: 'תקופת הניסיון עודכנה',
  trialExtension: 'תקופת הניסיון הוארכה',
  companyLimit: 'מכסת החברות עודכנה',
  seatLimit: 'מכסת המשתמשים עודכנה',
  state: 'מצב המנוי שונה',
};

type Outcome =
  | { readonly kind: 'changed'; readonly what: Changed; readonly overCapacityBy: number | null }
  | { readonly kind: 'failed'; readonly failure: ActionFailure };

function knownTenantStatuses(values: readonly string[] | undefined): readonly TenantStatus[] {
  const known: readonly string[] = TENANT_STATUSES;
  return (values ?? []).filter((v): v is TenantStatus => known.includes(v));
}

function knownSubscriptionStates(values: readonly string[] | undefined): readonly SubscriptionState[] {
  const known: readonly string[] = SUBSCRIPTION_STATES;
  return (values ?? []).filter((v): v is SubscriptionState => known.includes(v));
}

export function PlatformSubscriptionScreen({ tenantUid }: PlatformSubscriptionScreenProps): JSX.Element {
  const ctx = useWorkContext();
  const [planCode, setPlanCode] = useState('');
  const [skuCode, setSkuCode] = useState('');
  // QUANTITY IS NOT OPTIONAL. `platform.subscription.sku.add` refuses a body whose `quantity` is not
  // a number, and this form sent `{skuCode, reason}` — so "add an item to the subscription" was a
  // control that could only ever produce a 400. The operation's declared body is what surfaced it.
  const [skuQuantity, setSkuQuantity] = useState('1');
  const [trialEndsAt, setTrialEndsAt] = useState('');
  const [companyLimit, setCompanyLimit] = useState('');
  const [seatLimit, setSeatLimit] = useState('');
  const [unmeteredCompanies, setUnmeteredCompanies] = useState(false);
  const [unmeteredSeats, setUnmeteredSeats] = useState(false);
  const [toState, setToState] = useState<SubscriptionState>('active');
  const [reason, setReason] = useState('');
  const [sending, setSending] = useState(false);
  const [outcome, setOutcome] = useState<Outcome | null>(null);

  if (ctx.surface !== 'PLATFORM') {
    return <SurfaceNotice titleHe={TITLE_HE} requires="PLATFORM" actual={ctx.surface} />;
  }

  const tenant = (fixturesEnabled() ? fixtureTenants : []).find((t) => t.tenantUid === tenantUid) ?? null;
  const subscription = fixturesEnabled() ? fixtureSubscription : null;
  const back = { labelHe: 'כרטיס הלקוח', href: routes.platformTenants };

  if (tenant === null) {
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <StateBlock
          titleHe="הלקוח אינו זמין"
          bodyHe="לא נמצא הלקוח שאליו מפנה הקישור."
          tone="unavailable"
          nextStep={
            <a className="btn btn--primary" href={routes.platformTenants}>
              חזרה לרשימת הלקוחות
            </a>
          }
        />
      </AppShell>
    );
  }

  /**
   * ONE OUTCOME HANDLER, SEVEN OPERATIONS.
   *
   * This used to take a PATH FRAGMENT and a bare `unknown` body, which is two holes at once: the
   * address was assembled from a literal no scanner could attribute to a route, and the body was
   * unchecked against the handler that reads it. Closing the second hole found the SKU defect below.
   *
   * It now takes the CALL, so every form names its operation as a literal and the reachability scan
   * sees seven distinct actions rather than one function called seven times.
   */
  async function send(
    what: Changed,
    call: () => Promise<{ readonly overCapacityBy?: number }>,
  ): Promise<void> {
    setSending(true);
    setOutcome(null);
    try {
      // The limit routes answer with `overCapacityBy` — how far under current usage the new limit
      // sits. It is reported and never treated as an error: rule 1, nothing existing is degraded.
      const result = await call();
      setOutcome({ kind: 'changed', what, overCapacityBy: result?.overCapacityBy ?? null });
    } catch (err: unknown) {
      setOutcome({ kind: 'failed', failure: classifyActionError(err) });
    } finally {
      setSending(false);
    }
  }

  /** A commercial conflict, in the customer's own current state and the states it may move from. */
  function renderCommercialConflict(failure: Extract<ActionFailure, { kind: 'conflict' }>): JSX.Element {
    const refusal: TenantLifecycleRefusal = {
      reason: failure.body.error,
      currentStatus:
        knownTenantStatuses(failure.body.currentStatus === undefined ? [] : [failure.body.currentStatus])[0] ??
        null,
      permittedFrom: knownTenantStatuses(failure.body.permittedFrom),
    };
    const text = tenantLifecycleRefusalText(refusal);
    const states = knownSubscriptionStates(failure.body.permittedStates);
    return (
      <section className="denial" data-tone="blocked" role="alert">
        <h2 className="denial__title">{text.titleHe}</h2>
        <p className="denial__explanation">{text.explanationHe}</p>
        <p className="denial__explanation">{text.unchangedHe}</p>
        {/* The closed list of states, in Hebrew. 04 §13.2.1 exposes it on refusal for exactly this. */}
        {states.length === 0 ? null : (
          <p className="denial__explanation">{`המצבים האפשריים: ${subscriptionStatesHe(states)}.`}</p>
        )}
        <p className="denial__next-step">{text.nextStepHe}</p>
      </section>
    );
  }

  if (subscription === null) {
    // 08 §5.1: a customer with no subscription is a real state, and the operator's job here is to
    // create one — the first of the four provisioning steps, reachable outside the wizard too.
    return (
      <AppShell titleHe={TITLE_HE} backToParent={back}>
        <StateBlock
          titleHe="ללקוח אין עדיין מנוי"
          bodyHe="לא נרשם מנוי ללקוח הזה, ולכן אין לו מכסות ואין לו מודולים."
          tone="empty"
          nextStep={
            <a className="btn btn--primary" href={routes.platformTenantProvisioning}>
              מעבר להקמת מנוי
            </a>
          }
        />
      </AppShell>
    );
  }

  const state = subscriptionStateHe(subscription.state);

  return (
    <AppShell
      titleHe={TITLE_HE}
      backToParent={{ labelHe: 'כרטיס הלקוח', href: routes.platformTenant(tenant.tenantUid) }}
      provenanceHe="המסך מציג מכסות ומונים בלבד. אין בו מחיר, הנחה או סכום — נתוני כסף אינם חלק מהזכאות."
    >
      <FactList
        facts={[
          { labelHe: 'הלקוח', value: tenant.legalName },
          { labelHe: 'התכנית', value: subscription.planNameHe },
          { labelHe: 'מצב המנוי', value: <StatusChip labelHe={state.labelHe} tone={state.tone} /> },
          {
            labelHe: 'תקופת המנוי',
            value: (
              <span dir="ltr">
                {formatInstantHe(subscription.periodStartUtc)} — {formatInstantHe(subscription.periodEndUtc)}
              </span>
            ),
          },
          {
            labelHe: 'סיום תקופת ניסיון',
            value: formatInstantHe(subscription.trialEndsAtUtc),
            absentHe: 'אין תקופת ניסיון',
          },
        ]}
      />

      <CapacityPanel capacity={subscription.capacity} captionHe="מכסות שנרכשו" />

      {/*
        Module CODES, never SKUs. 08 D12: the gate never sees a SKU, so a screen that listed them
        would be describing a commercial object as though it were an access one.
      */}
      <section className="tiles">
        {subscription.licensedModules.map((code) => (
          <article key={code} className="tile">
            <h2 className="tile__title">{code}</h2>
            <p className="tile__lead">נרכש במנוי של הלקוח.</p>
          </article>
        ))}
      </section>

      <div className="actions-row">
        <a className="btn" href={routes.platformTenantCommercial(tenant.tenantUid)}>
          מסחר, הנחות ואישורים
        </a>
      </div>

      {outcome === null ? null : outcome.kind === 'changed' ? (
        <StateBlock
          titleHe={CHANGED_HE[outcome.what]}
          bodyHe={
            outcome.overCapacityBy === null || outcome.overCapacityBy === 0
              ? 'השינוי נרשם ביומן יחד עם הסיבה ועם מי שביצע אותו.'
              : `השינוי נרשם. הלקוח נמצא כעת ${outcome.overCapacityBy} מעל המכסה שנקבעה — כל מה שקיים ממשיך לעבוד, ולא ניתן להוסיף חדשים.`
          }
          nextStep={<p>המספרים במסך יתעדכנו בטעינה הבאה.</p>}
        />
      ) : (
        <ActionFailureNotice failure={outcome.failure} renderConflict={renderCommercialConflict} />
      )}

      {/* ── ENTITLEMENT: the plan, the modules, the trial and the two limits ────────────────── */}
      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('plan', () =>
            callOperation('platform.subscription.plan.assign', {
              params: { tenant_uid: tenantUid },
              body: { planCode, reason },
            }),
          );
        }}
      >
        <h2 className="form__title">התכנית והמודולים</h2>
        <label className="field">
          <span className="field__label">קוד התכנית</span>
          <input
            className="field__input"
            name="planCode"
            dir="ltr"
            value={planCode}
            onChange={(e) => setPlanCode(e.currentTarget.value)}
          />
          <span className="field__hint">שיוך תכנית קובע מה נכלל במנוי. הוא אינו משנה מחיר במסך הזה.</span>
        </label>
        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending || planCode === ''}>
            שיוך התכנית
          </button>
        </div>
      </form>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('sku', () =>
            callOperation('platform.subscription.sku.add', {
              params: { tenant_uid: tenantUid },
              body: { skuCode, quantity: Number(skuQuantity) },
            }),
          );
        }}
      >
        <h2 className="form__title">הוספת פריט למנוי</h2>
        <label className="field">
          <span className="field__label">קוד הפריט</span>
          <input
            className="field__input"
            name="skuCode"
            dir="ltr"
            value={skuCode}
            onChange={(e) => setSkuCode(e.currentTarget.value)}
          />
          <span className="field__hint">
            הפריט נפתח למודולים שהוא כולל. המודולים הם מה שהמערכת בודקת, והפריט עצמו אינו מגיע לבדיקה.
          </span>
        </label>
        <label className="field">
          <span className="field__label">כמות</span>
          <input
            className="field__input"
            name="skuQuantity"
            dir="ltr"
            inputMode="numeric"
            value={skuQuantity}
            onChange={(e) => setSkuQuantity(e.currentTarget.value)}
          />
          <span className="field__hint">
            הכמות נשלחת לשרת ואינה נגזרת מהעדר ערך. בלעדיה הבקשה נדחית.
          </span>
        </label>
        <div className="actions-row">
          <button
            type="submit"
            className="btn btn--primary"
            disabled={
              sending || skuCode === '' || skuQuantity === '' || Number.isNaN(Number(skuQuantity))
            }
          >
            הוספת הפריט
          </button>
        </div>
      </form>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('trial', () =>
            callOperation('platform.trial.set', {
              params: { tenant_uid: tenantUid },
              body: {
                // `null` is a MEANINGFUL value here — it clears the trial — and it is not the
                // same as an empty field being ignored. The control says which one it is sending.
                trialEndsAt: trialEndsAt === '' ? null : trialEndsAt,
                reason,
              },
            }),
          );
        }}
      >
        <h2 className="form__title">תקופת ניסיון</h2>
        <label className="field">
          <span className="field__label">מועד סיום תקופת הניסיון</span>
          <input
            className="field__input"
            name="trialEndsAt"
            dir="ltr"
            value={trialEndsAt}
            onChange={(e) => setTrialEndsAt(e.currentTarget.value)}
          />
          <span className="field__hint">שדה ריק מבטל את תקופת הניסיון. זו פעולה, ולא היעדר פעולה.</span>
        </label>
        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending}>
            קביעת תקופת הניסיון
          </button>
          <button
            type="button"
            className="btn"
            disabled={sending || trialEndsAt === ''}
            onClick={() =>
              void send('trialExtension', () =>
                callOperation('platform.trial.extend', {
                  params: { tenant_uid: tenantUid },
                  body: { trialEndsAt, reason },
                }),
              )
            }
          >
            הארכת תקופת הניסיון
          </button>
        </div>
        <p className="form__note">
          הארכה היא פעולה נפרדת מקביעה, כדי שביומן יירשם מה בדיוק נעשה: הארכה של תקופה קיימת אינה
          פתיחה של תקופה חדשה.
        </p>
      </form>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('companyLimit', () =>
            callOperation('platform.limit.company.set', {
              params: { tenant_uid: tenantUid },
              body: { limit: unmeteredCompanies ? null : Number(companyLimit), reason },
            }),
          );
        }}
      >
        <h2 className="form__title">מכסת החברות</h2>
        <label className="field">
          <span className="field__label">מספר החברות שנרכשו</span>
          <input
            className="field__input"
            name="companyLimit"
            dir="ltr"
            inputMode="numeric"
            value={companyLimit}
            disabled={unmeteredCompanies}
            onChange={(e) => setCompanyLimit(e.currentTarget.value)}
          />
        </label>
        <label className="field field--inline">
          <input
            type="checkbox"
            name="unmeteredCompanies"
            checked={unmeteredCompanies}
            onChange={(e) => setUnmeteredCompanies(e.currentTarget.checked)}
          />
          <span className="field__label">ללא הגבלת מספר חברות</span>
        </label>
        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending}>
            קביעת המכסה
          </button>
        </div>
        <p className="form__note">
          אפשר לקבוע מכסה נמוכה ממה שכבר בשימוש. במקרה כזה כל החברות הקיימות ממשיכות לעבוד במלואן,
          ורק הקמה של חברה נוספת נחסמת. שום דבר אינו נמחק.
        </p>
      </form>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('seatLimit', () =>
            callOperation('platform.limit.seat.set', {
              params: { tenant_uid: tenantUid },
              body: { limit: unmeteredSeats ? null : Number(seatLimit), reason },
            }),
          );
        }}
      >
        <h2 className="form__title">מכסת המשתמשים</h2>
        <label className="field">
          <span className="field__label">מספר המושבים שנרכשו</span>
          <input
            className="field__input"
            name="seatLimit"
            dir="ltr"
            inputMode="numeric"
            value={seatLimit}
            disabled={unmeteredSeats}
            onChange={(e) => setSeatLimit(e.currentTarget.value)}
          />
        </label>
        <label className="field field--inline">
          <input
            type="checkbox"
            name="unmeteredSeats"
            checked={unmeteredSeats}
            onChange={(e) => setUnmeteredSeats(e.currentTarget.checked)}
          />
          <span className="field__label">ללא הגבלת מספר משתמשים</span>
        </label>
        <div className="actions-row">
          <button type="submit" className="btn btn--primary" disabled={sending}>
            קביעת המכסה
          </button>
        </div>
        <p className="form__note">
          אותו כלל חל כאן: מכסה נמוכה מהשימוש הקיים אינה משביתה אף משתמש, והיא חוסמת הזמנה של משתמש
          נוסף בלבד.
        </p>
      </form>

      <form
        className="form"
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          void send('state', () =>
            callOperation('platform.subscription.state.change', {
              params: { tenant_uid: tenantUid },
              body: { toState, reason },
            }),
          );
        }}
      >
        <h2 className="form__title">שינוי מצב המנוי</h2>
        <p className="form__lead">
          שינוי ידני של מצב המנוי עוקף את המסלול הרגיל, ולכן הוא שמור לבעלים של הפלטפורמה ונרשם
          בהיסטוריית המצבים של המנוי יחד עם הסיבה.
        </p>
        <fieldset className="field field--choice">
          <legend className="field__label">המצב החדש</legend>
          {SUBSCRIPTION_STATES.map((candidate) => (
            <label key={candidate} className="choice">
              <input
                type="radio"
                name="toState"
                value={candidate}
                checked={toState === candidate}
                onChange={() => setToState(candidate)}
              />
              <span>{subscriptionStateHe(candidate).labelHe}</span>
            </label>
          ))}
        </fieldset>
        <div className="actions-row">
          <button type="submit" className="btn btn--irreversible" disabled={sending || reason === ''}>
            שינוי מצב המנוי
          </button>
        </div>
      </form>

      {/*
        ONE reason field for every form above, and it is mandatory on all of them. A commercial
        change with no written reason is a change nobody can explain six months later, and the server
        refuses each of these without one.
      */}
      <label className="field">
        <span className="field__label">סיבת השינוי המסחרי</span>
        <input
          className="field__input"
          name="commercial-reason"
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
        />
        <span className="field__hint">
          הסיבה נשלחת עם כל אחת מהפעולות במסך הזה ונרשמת ביומן יחד עם מי שביצע אותה.
        </span>
      </label>
    </AppShell>
  );
}
